(() => {
  if (window.__AIMA_COUNTER_V15__) return;
  window.__AIMA_COUNTER_V15__ = true;

  const DEBUG = false;
  const log = (...a) => DEBUG && console.log("[AIMA]", ...a);
  const err = (...a) => DEBUG && console.error("[AIMA]", ...a);

  // IMPORTANT:
  // This only avoids Avada's fetch monkey-patch if this script loads BEFORE Avada.
  const _fetch = (window.__nativeFetch = window.__nativeFetch || window.fetch.bind(window));

  const MAX_COUNT = 2000;
  const STEP_MS = 60;
  const FETCH_TIMEOUT_MS = 10000;
  const SYNC_DEBOUNCE_MS = 500;
  const MIN_MUTATION_GAP_MS = 700;
  const CART_CACHE_MS = 6000;

  const COUNTER_SELECTOR = ".counter-container[data-aima-counter]";
  const GRID_ITEM_SELECTOR = "product-grid-item[data-product-grid-item]";
  const RECENTLY_VIEWED_GRID_SELECTOR =
    '[data-section-type="recently-viewed"] .grid-item.grid-product';
  const FLEISCH_SELECTOR = ".aima-fleisch-widget";

  // ─── Helpers ──────────────────────────────────────────────────────────────
  const clamp = (val, min, max) => {
    let n = Number(val);
    if (!Number.isFinite(n)) n = 0;
    if (n < min) n = min;
    if (n > max) n = max;
    return n;
  };

  const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

  const fetchWithTimeout = (url, options = {}, ms = FETCH_TIMEOUT_MS) => {
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), ms);
    return _fetch(url, { ...options, signal: ctrl.signal }).finally(() =>
      clearTimeout(timer)
    );
  };

  const parseRetryAfterMs = (res) => {
    const h = res?.headers?.get?.("Retry-After");
    if (!h) return 0;
    const n = Number(h);
    if (Number.isFinite(n) && n > 0) return n * 1000;
    return 0;
  };

  // ─── Rate-limit cooldown / circuit breaker ────────────────────────────────
  let rateLimitedUntil = 0;
  let rateLimitTimer = null;

  const isRateLimitedNow = () => Date.now() < rateLimitedUntil;

  const enterRateLimitCooldown = (ms = 2500) => {
    const until = Date.now() + ms;
    rateLimitedUntil = Math.max(rateLimitedUntil, until);

    if (rateLimitTimer) clearTimeout(rateLimitTimer);
    rateLimitTimer = setTimeout(() => {
      rateLimitTimer = null;
      if (!syncRunning) {
        scheduleBatchSync();
      } else {
        syncPending = true;
      }
    }, Math.max(0, rateLimitedUntil - Date.now()));
  };

  const fetchWithRetry = async (url, options = {}, maxRetries = 2) => {
    let attempt = 0;

    while (true) {
      if (isRateLimitedNow()) {
        const wait = Math.max(0, rateLimitedUntil - Date.now());
        err(`AIMA cooldown active for ${url}, waiting ${wait}ms`);
        await sleep(wait);
      }

      const res = await fetchWithTimeout(url, options);

      if (res.status !== 429) return res;

      const retryAfter = parseRetryAfterMs(res);
      const backoff = retryAfter || (2000 + attempt * 1000);

      err(`429 on ${url}; backing off for ${backoff}ms`);
      enterRateLimitCooldown(backoff);

      if (attempt >= maxRetries) {
        return res;
      }

      await sleep(backoff);
      attempt += 1;
    }
  };

  // ─── Page guard ───────────────────────────────────────────────────────────
  const isMeatCollectionPage = () => {
    const p = (location.pathname || "").toLowerCase();
    if (p.includes("/products/")) return false;
    return (
      p.includes("/collections/fleisch") ||
      p.includes("/collections/gefluegel") ||
      p.includes("/collections/geflugel") ||
      p.includes("/collections/gefl")
    );
  };

  const removeInjectedCountersOnMeatCollections = () => {
    if (!isMeatCollectionPage()) return;
    document
      .querySelectorAll(".counter-container[data-aima-counter], .grid-item-aima-counter")
      .forEach((el) => el.remove());
  };

  // ─── Cart cache ───────────────────────────────────────────────────────────
  let cartCache = null;
  let cartCacheTime = 0;
  let cartFetchPromise = null;

  const invalidateCart = () => {
    cartCache = null;
    cartCacheTime = 0;
    cartFetchPromise = null;
  };

  const getCart = (forceFresh = false) => {
    const now = Date.now();

    if (!forceFresh && cartCache && now - cartCacheTime < CART_CACHE_MS) {
      return Promise.resolve(cartCache);
    }

    if (cartFetchPromise) return cartFetchPromise;

    cartFetchPromise = fetchWithRetry("/cart.js", {
      credentials: "same-origin",
      headers: { Accept: "application/json" }
    })
      .then((res) => {
        if (!res.ok) throw new Error(`cart.js ${res.status}`);
        return res.json();
      })
      .then((cart) => {
        cartCache = cart;
        cartCacheTime = Date.now();
        cartFetchPromise = null;
        return cart;
      })
      .catch((e) => {
        err("getCart error:", e);
        cartFetchPromise = null;
        return cartCache || { items: [], item_count: 0 };
      });

    return cartFetchPromise;
  };

  const findLineItem = (cart, variantId) => {
    const id = Number(variantId);
    if (!cart?.items) return null;
    return cart.items.find((i) => Number(i.variant_id) === id) ?? null;
  };

  const dispatchCartUpdated = (cart) => {
    document.dispatchEvent(new CustomEvent("cart:updated", { detail: { cart } }));
    document.dispatchEvent(new CustomEvent("aima:cart:updated", { detail: { cart } }));
  };

  // ─── Bubble helpers (LOCAL-FIRST) ─────────────────────────────────────────
  let externalCartCountBase = 0;

  const updateHeaderCartCountValue = (rawCount) => {
    const n = clamp(rawCount, 0, 999999);

    [
      ...document.querySelectorAll(".cart-link__bubble"),
      ...document.querySelectorAll(".site-nav__cart-count"),
      ...document.querySelectorAll("[data-cart-count]"),
      ...document.querySelectorAll("[data-cart-count-bubble]")
    ].forEach((root) => {
      const node =
        root.matches(".cart-link__bubble-num,.cart-count,.count,[data-cart-count]")
          ? root
          : root.querySelector(".cart-link__bubble-num,.cart-count,.count,[data-cart-count]");

      if (node) {
        node.textContent = String(n);
        node.setAttribute("data-count", String(n));
      } else if (!root.childElementCount) {
        root.textContent = String(n);
      }

      root.setAttribute("data-count", String(n));

      if (root.classList.contains("cart-link__bubble")) {
        root.classList.toggle("cart-link__bubble--visible", n > 0);
      }
    });
  };

  const getDesiredTrackedTotal = () => {
    let total = 0;
    desiredQty.forEach((qty) => {
      total += clamp(qty, 0, MAX_COUNT);
    });
    return total;
  };

  const getTrackedServerTotal = (cart) => {
    if (!cart?.items?.length) return 0;

    let total = 0;
    cart.items.forEach((item) => {
      const key = String(item.variant_id);
      if (desiredQty.has(key)) {
        total += clamp(item.quantity, 0, MAX_COUNT);
      }
    });
    return total;
  };

  const recalcExternalCartCountBase = (cart) => {
    const itemCount = clamp(Number(cart?.item_count || 0), 0, 999999);
    const trackedServerTotal = getTrackedServerTotal(cart);
    externalCartCountBase = Math.max(0, itemCount - trackedServerTotal);
  };

  const renderBubbleFromDesiredQty = () => {
    const total = externalCartCountBase + getDesiredTrackedTotal();
    updateHeaderCartCountValue(total);
  };

  const updateHeaderCartCount = (cart) => {
    recalcExternalCartCountBase(cart);
    renderBubbleFromDesiredQty();
  };

  // ─── Visual state ─────────────────────────────────────────────────────────
  const stageOf = (v) => {
    v = clamp(v, 0, MAX_COUNT);
    if (v <= 0) return "stage-0";
    if (v === 1) return "stage-3";
    return "stage-4";
  };

  const setStage = (container, stage) => {
    const counter = container.querySelector(".aima-counter");
    if (counter) counter.className = `aima-counter ${stage}`.trim();
  };

  const clearAnimation = (container) => {
    (container._aimaTimers || []).forEach(clearTimeout);
    container._aimaTimers = [];
    container.dataset.animating = "0";
  };

  const runOpenSequence = (container) => {
    clearAnimation(container);

    if (window.matchMedia("(max-width: 480px)").matches) {
      setStage(container, "stage-3");
      return;
    }

    container.dataset.animating = "1";
    setStage(container, "stage-plus");

    container._aimaTimers = [
      setTimeout(() => setStage(container, "stage-1"), STEP_MS * 0.6),
      setTimeout(() => setStage(container, "stage-2"), STEP_MS * 1.2),
      setTimeout(() => {
        setStage(container, "stage-3");
        container.dataset.animating = "0";
        container._aimaTimers = [];
      }, STEP_MS * 2)
    ];
  };

  const readCount = (countEl) => {
    if (!countEl) return 0;
    const digits = String(countEl.value ?? "").replace(/[^\d]/g, "");
    return digits ? clamp(parseInt(digits, 10), 0, MAX_COUNT) : 0;
  };

  const writeCount = (countEl, value) => {
    if (countEl) countEl.value = String(clamp(value, 0, MAX_COUNT));
  };

  const applyVisualCount = (container, count, fromCartInit = false) => {
    const plusBtn = container.querySelector(".aima-plus");
    const countEl = container.querySelector(".aima-count");
    if (!plusBtn || !countEl) return;

    const prev = clamp(Number(container.dataset.prevCount || 0), 0, MAX_COUNT);
    count = clamp(count, 0, MAX_COUNT);
    container.dataset.prevCount = String(count);
    writeCount(countEl, count);

    if (count <= 0) {
      clearAnimation(container);
      setStage(container, "stage-0");
      plusBtn.disabled = false;
      plusBtn.style.opacity = "1";
      plusBtn.style.cursor = "pointer";
      plusBtn.style.pointerEvents = "auto";
    } else {
      if (!fromCartInit && prev === 0 && count > 0) {
        runOpenSequence(container);
      } else if (container.dataset.animating !== "1") {
        setStage(container, stageOf(count));
      }

      const atMax = count >= MAX_COUNT;
      plusBtn.disabled = atMax;
      plusBtn.style.opacity = atMax ? "0.5" : "1";
      plusBtn.style.cursor = atMax ? "default" : "pointer";
      plusBtn.style.pointerEvents = "";
    }
  };

  // ─── Variant → counter registry ───────────────────────────────────────────
  const variantCounters = new Map();

  const registerCounter = (variantId, container) => {
    const k = String(variantId);
    if (!variantCounters.has(k)) variantCounters.set(k, new Set());
    variantCounters.get(k).add(container);
  };

  const applyVisualCountAll = (variantId, count, fromCartInit = false) => {
    const k = String(variantId);
    const set = variantCounters.get(k);
    if (!set) return;

    for (const c of [...set]) {
      if (!c || !c.isConnected) {
        set.delete(c);
        continue;
      }
      applyVisualCount(c, count, fromCartInit);
    }

    if (!set.size) variantCounters.delete(k);
  };

  // ─── Desired qty ──────────────────────────────────────────────────────────
  const desiredQty = new Map();

  // ─── Global batch sync ────────────────────────────────────────────────────
  let syncTimer = null;
  let syncRunning = false;
  let syncPending = false;
  let lastMutationAt = 0;

  const waitForMutationGap = async () => {
    const now = Date.now();
    const wait = Math.max(0, MIN_MUTATION_GAP_MS - (now - lastMutationAt));
    if (wait > 0) await sleep(wait);
  };

  const runBatchSync = async () => {
    if (syncRunning) {
      syncPending = true;
      return;
    }

    if (isRateLimitedNow()) {
      syncPending = true;
      scheduleBatchSync();
      return;
    }

    syncRunning = true;
    syncPending = false;

    try {
      const snapshot = new Map(desiredQty);

      let cart = await getCart(false);
      if (!cart || !Array.isArray(cart.items)) {
        cart = await getCart(true);
      }

      const updates = {};
      const toAdd = [];

      for (const [variantId, wantedRaw] of snapshot.entries()) {
        const wanted = clamp(wantedRaw, 0, MAX_COUNT);
        const line = findLineItem(cart, variantId);
        const have = line ? clamp(line.quantity, 0, MAX_COUNT) : 0;

        if (have === wanted) continue;

        if (line) {
          updates[variantId] = wanted;
        } else if (wanted > 0) {
          toAdd.push({ id: Number(variantId), quantity: wanted });
        }
      }

      let finalCart = cart;

      if (toAdd.length > 0) {
        await waitForMutationGap();

        const addRes = await fetchWithRetry("/cart/add.js", {
          method: "POST",
          credentials: "same-origin",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({ items: toAdd })
        });

        lastMutationAt = Date.now();

        if (!addRes.ok) throw new Error(`add.js ${addRes.status}`);
        await addRes.json();

        invalidateCart();
        finalCart = await getCart(true);
      }

      if (Object.keys(updates).length > 0) {
        await waitForMutationGap();

        const updateRes = await fetchWithRetry("/cart/update.js", {
          method: "POST",
          credentials: "same-origin",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({ updates })
        });

        lastMutationAt = Date.now();

        if (!updateRes.ok) throw new Error(`update.js ${updateRes.status}`);
        finalCart = await updateRes.json();
      }

      cartCache = finalCart;
      cartCacheTime = Date.now();

      for (const [variantId, snapQtyRaw] of snapshot.entries()) {
        const snapQty = clamp(snapQtyRaw, 0, MAX_COUNT);
        const serverLine = findLineItem(finalCart, variantId);
        const serverQty = serverLine ? clamp(serverLine.quantity, 0, MAX_COUNT) : 0;
        const currentDesired = clamp(desiredQty.get(variantId) ?? 0, 0, MAX_COUNT);

        if (currentDesired === snapQty && serverQty !== currentDesired) {
          desiredQty.set(variantId, serverQty);
          applyVisualCountAll(variantId, serverQty, true);
        }
      }

      recalcExternalCartCountBase(finalCart);
      renderBubbleFromDesiredQty();
      dispatchCartUpdated(finalCart);
    } catch (e) {
      err("AIMA batch sync error:", e);

      try {
        invalidateCart();
        const freshCart = await getCart(true);
        cartCache = freshCart;
        cartCacheTime = Date.now();

        desiredQty.forEach((_, variantId) => {
          const line = findLineItem(freshCart, variantId);
          const real = line ? clamp(line.quantity, 0, MAX_COUNT) : 0;
          desiredQty.set(variantId, real);
          applyVisualCountAll(variantId, real, true);
        });

        recalcExternalCartCountBase(freshCart);
        renderBubbleFromDesiredQty();
      } catch {
        // silent
      }
    } finally {
      syncRunning = false;

      if (syncPending) {
        syncPending = false;
        scheduleBatchSync();
      }
    }
  };

  const scheduleBatchSync = () => {
    if (syncTimer) clearTimeout(syncTimer);

    const delay = isRateLimitedNow()
      ? Math.max(SYNC_DEBOUNCE_MS, rateLimitedUntil - Date.now())
      : SYNC_DEBOUNCE_MS;

    syncTimer = setTimeout(() => {
      syncTimer = null;
      runBatchSync();
    }, delay);
  };

  // ─── Counter element init ─────────────────────────────────────────────────
  const initCounterElement = async (container) => {
    if (!container || container.dataset.counterInited === "1") return;

    const variantId = container.dataset.variantId;
    if (!variantId) return;

    const key = String(variantId);

    container.dataset.counterInited = "1";
    registerCounter(variantId, container);

    const countEl = container.querySelector(".aima-count");
    const minusBtn = container.querySelector(".aima-left");
    const plusBtn = container.querySelector(".aima-plus");

    const handleClick = (delta) => {
      const current = clamp(desiredQty.get(key) ?? readCount(countEl), 0, MAX_COUNT);
      const next = clamp(current + delta, 0, MAX_COUNT);
      if (next === current) return;

      desiredQty.set(key, next);
      applyVisualCountAll(variantId, next);
      renderBubbleFromDesiredQty();
      scheduleBatchSync();
    };

    if (minusBtn) {
      minusBtn.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();
        handleClick(-1);
      });
    }

    if (plusBtn) {
      plusBtn.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();
        handleClick(+1);
      });
    }

    if (countEl) {
      ["click", "mousedown", "pointerdown", "touchstart"].forEach((evt) =>
        countEl.addEventListener(evt, (e) => e.stopPropagation(), true)
      );

      countEl.addEventListener("input", () => {
        const v = readCount(countEl);
        writeCount(countEl, v);

        if (v <= 0) {
          clearAnimation(container);
          setStage(container, "stage-0");
        } else if (container.dataset.animating !== "1") {
          setStage(container, stageOf(v));
        }
      });

      countEl.addEventListener("keydown", (e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          countEl.blur();
        }

        if (e.key === "Escape") {
          e.preventDefault();
          writeCount(countEl, desiredQty.get(key) ?? 0);
          countEl.blur();
        }
      });

      countEl.addEventListener("blur", () => {
        const v = clamp(readCount(countEl), 0, MAX_COUNT);
        desiredQty.set(key, v);
        applyVisualCountAll(variantId, v);
        renderBubbleFromDesiredQty();
        scheduleBatchSync();
      });
    }

    // Safe init: do not overwrite fresh local intent
    getCart(false)
      .then((cart) => {
        const line = findLineItem(cart, variantId);
        const qty = line ? clamp(line.quantity, 0, MAX_COUNT) : 0;

        if (!desiredQty.has(key)) {
          desiredQty.set(key, qty);
          applyVisualCountAll(variantId, qty, true);
        } else {
          applyVisualCount(container, desiredQty.get(key) ?? 0, true);
        }

        recalcExternalCartCountBase(cart);
        renderBubbleFromDesiredQty();
      })
      .catch(() => {
        if (!desiredQty.has(key)) {
          desiredQty.set(key, 0);
          applyVisualCountAll(variantId, 0, true);
        } else {
          applyVisualCount(container, desiredQty.get(key) ?? 0, true);
        }

        renderBubbleFromDesiredQty();
      });
  };

  window.AIMA = window.AIMA || {};
  window.AIMA.initCounterElement = initCounterElement;

  const scanExistingCounters = () => {
    document
      .querySelectorAll(`${COUNTER_SELECTOR}:not([data-counter-inited])`)
      .forEach(initCounterElement);
  };

  // ─── Product data helpers ─────────────────────────────────────────────────
  const variantCache = new Map();
  const meatCache = new Map();

  const fetchProductJSON = async (handle) => {
    handle = String(handle || "").trim();
    if (!handle) return null;

    try {
      const res = await fetchWithRetry(`/products/${handle}.js`, {
        credentials: "same-origin",
        headers: { Accept: "application/json" }
      });
      if (!res.ok) throw new Error(`product.js ${res.status}`);
      return res.json();
    } catch {
      return null;
    }
  };

  const fetchDefaultVariantId = async (handle) => {
    if (variantCache.has(handle)) return variantCache.get(handle);
    const product = await fetchProductJSON(handle);
    const id = product?.variants?.[0]?.id ?? null;
    if (id) variantCache.set(handle, id);
    return id;
  };

  const isMeatByHandle = async (handle) => {
    if (meatCache.has(handle)) return meatCache.get(handle);
    const product = await fetchProductJSON(handle);
    const result = Array.isArray(product?.tags) && product.tags.includes("Gr_Button");
    meatCache.set(handle, result);
    return result;
  };

  const inferHandle = (gridItemEl) => {
    const link =
      gridItemEl.querySelector("a.grid-item__link") ||
      gridItemEl.querySelector("a[href*='/products/']");
    if (!link) return null;

    try {
      const url = new URL(link.getAttribute("href"), location.origin);
      const parts = url.pathname.split("/").filter(Boolean);
      const idx = parts.lastIndexOf("products");
      return idx !== -1 && parts[idx + 1]
        ? parts[idx + 1]
        : parts[parts.length - 1] || null;
    } catch {
      return null;
    }
  };

  const normalizeGridItem = (el) => {
    if (!el) return null;
    if (el.matches?.(GRID_ITEM_SELECTOR)) return el;

    if (el.matches?.("is-land.grid-item.grid-product")) {
      const inner = el.querySelector(GRID_ITEM_SELECTOR);
      if (inner) return inner;
    }

    if (el.matches?.(RECENTLY_VIEWED_GRID_SELECTOR)) return el;

    return el.querySelector?.(`${GRID_ITEM_SELECTOR}, ${RECENTLY_VIEWED_GRID_SELECTOR}`) ?? null;
  };

  // ─── Counter DOM builder ──────────────────────────────────────────────────
  const buildCounterElement = (variantId, productId) => {
    const wrapper = document.createElement("div");
    wrapper.className = "counter-container grid-item-aima-counter";
    wrapper.setAttribute("data-aima-counter", "");
    wrapper.dataset.variantId = String(variantId);
    if (productId) wrapper.dataset.productId = String(productId);

    const counter = document.createElement("div");
    counter.className = "aima-counter stage-0";

    const pill = document.createElement("div");
    pill.className = "aima-pill";

    const row = document.createElement("div");
    row.className = "aima-row";

    const minusBtn = document.createElement("button");
    minusBtn.type = "button";
    minusBtn.className = "aima-left";
    minusBtn.setAttribute("aria-label", "Decrease");
    minusBtn.innerHTML = '<i class="fas fa-trash-alt"></i><i class="fas fa-minus"></i>';

    const input = document.createElement("input");
    input.className = "aima-count";
    input.type = "text";
    input.inputMode = "numeric";
    input.pattern = "[0-9]*";
    input.value = "0";
    input.setAttribute("aria-label", "Quantity");
    input.autocomplete = "off";

    row.appendChild(minusBtn);
    row.appendChild(input);
    pill.appendChild(row);
    counter.appendChild(pill);

    const plusBtn = document.createElement("button");
    plusBtn.type = "button";
    plusBtn.className = "aima-plus";
    plusBtn.setAttribute("aria-label", "Increase");
    plusBtn.innerHTML = '<i class="fas fa-plus"></i>';

    counter.appendChild(plusBtn);
    wrapper.appendChild(counter);
    return wrapper;
  };

  // ─── Grid item injection ──────────────────────────────────────────────────
  const ensureCounterForGridItem = async (rawEl) => {
    const gridItemEl = normalizeGridItem(rawEl);
    if (!gridItemEl) return;

    const isRecentlyViewed = !!rawEl.closest('[data-section-type="recently-viewed"]');

    if (isMeatCollectionPage() && !isRecentlyViewed) {
      gridItemEl.dataset.aimaCounterAttached = "1";
      return;
    }

    if (gridItemEl.dataset.aimaCounterAttached === "1") return;

    if (
      gridItemEl.querySelector(FLEISCH_SELECTOR) ||
      gridItemEl.getAttribute("data-meat-product") === "true" ||
      gridItemEl.getAttribute("data-meat-product") === "1"
    ) {
      gridItemEl.dataset.aimaCounterAttached = "1";
      return;
    }

    gridItemEl.dataset.aimaCounterAttached = "1";

    const imageWrap =
      gridItemEl.querySelector(".grid-product__image-wrap") ||
      gridItemEl.querySelector(".usf-sr-product-image-container");
    if (!imageWrap) return;

    const existing = imageWrap.querySelector(COUNTER_SELECTOR);
    if (existing) {
      initCounterElement(existing);
      return;
    }

    if (!gridItemEl.dataset.productHandle) {
      const inferred = inferHandle(gridItemEl);
      if (inferred) gridItemEl.dataset.productHandle = inferred;
    }

    const handle = gridItemEl.dataset.productHandle;
    const productId = gridItemEl.dataset.productId;
    if (!handle) return;

    if (await isMeatByHandle(handle)) return;
    if (imageWrap.querySelector(FLEISCH_SELECTOR)) return;

    const variantId = await fetchDefaultVariantId(handle);
    if (!variantId) return;

    const el = buildCounterElement(variantId, productId);
    imageWrap.prepend(el);
    initCounterElement(el);
    log("Counter injected for:", handle);
  };

  const scanGridItems = () => {
    document
      .querySelectorAll(
        `${GRID_ITEM_SELECTOR}, ${RECENTLY_VIEWED_GRID_SELECTOR}, is-land.grid-item.grid-product`
      )
      .forEach(ensureCounterForGridItem);
  };

  // ─── Click blocker ────────────────────────────────────────────────────────
  document.addEventListener(
    "click",
    (e) => {
      const t = e.target;
      if (!(t instanceof Element)) return;
      if (!t.closest(COUNTER_SELECTOR) && !t.closest(FLEISCH_SELECTOR)) return;
      const anchor = t.closest("a");
      if (anchor && e.cancelable) e.preventDefault();
    },
    true
  );

  // ─── Mutation Observer ────────────────────────────────────────────────────
  const pendingGridItems = new Set();
  const pendingCounters = new Set();
  let mutationRafId = null;

  const flushPendingMutations = () => {
    mutationRafId = null;

    const gridItems = Array.from(pendingGridItems);
    const counters = Array.from(pendingCounters);
    pendingGridItems.clear();
    pendingCounters.clear();

    gridItems.forEach((el) => ensureCounterForGridItem(el));
    counters.forEach((c) => initCounterElement(c));

    if (gridItems.length) scanGridItems();
  };

  const setupMutationObserver = () => {
    const target = document.body;
    if (!target || !window.MutationObserver) return;

    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        mutation.addedNodes.forEach((node) => {
          if (!(node instanceof HTMLElement)) return;

          // Ignore nodes that are part of a counter or fleisch widget
          if (node.closest?.(COUNTER_SELECTOR)) return;
          if (node.classList?.contains("aima-fleisch-widget")) return;

          // Check if the node itself is a grid item
          if (
            node.matches?.(GRID_ITEM_SELECTOR) ||
            node.matches?.("is-land.grid-item.grid-product") ||
            node.matches?.(RECENTLY_VIEWED_GRID_SELECTOR)
          ) {
            pendingGridItems.add(node);
          } else {
            // Check inside the node for grid items
            node.querySelectorAll?.(
              `${GRID_ITEM_SELECTOR}, is-land.grid-item.grid-product, ${RECENTLY_VIEWED_GRID_SELECTOR}`
            ).forEach((el) => pendingGridItems.add(el));
          }

          // Check if the node itself is an uninitialised counter
          if (node.matches?.(COUNTER_SELECTOR)) {
            pendingCounters.add(node);
          } else {
            node.querySelectorAll?.(COUNTER_SELECTOR)
              .forEach((c) => pendingCounters.add(c));
          }
        });
      }

      if ((pendingGridItems.size || pendingCounters.size) && !mutationRafId) {
        mutationRafId = requestAnimationFrame(flushPendingMutations);
      }
    });

    observer.observe(target, { childList: true, subtree: true });
  };

  // ─── Boot ─────────────────────────────────────────────────────────────────
  const init = () => {
    removeInjectedCountersOnMeatCollections();
    scanExistingCounters();
    scanGridItems();
    setupMutationObserver();
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();